import { useState } from 'react';
import { SPACE_TYPES, SPACE_TYPE_ORDER } from '../lib/space-types';

export default function InviteModal({
  inviteConsentChecked, inviteEmail, inviteError, inviteSending,
  inviteSpaceComment, inviteSpaceProfRole, inviteSpaceTitle, inviteSpaceType,
  inviteSuccess, sendSharedInvite,
  setInviteConsentChecked, setInviteEmail, setInviteError,
  setInviteSpaceComment, setInviteSpaceProfRole, setInviteSpaceTitle, setInviteSpaceType,
  setShowInviteModal,
  inviteShiftMode, setInviteShiftMode,
}) {
  const [customRole, setCustomRole] = useState('');

  function handleSend() {
    if (!inviteEmail.trim()) { setInviteError('Email address is required.'); return; }
    if (!inviteSpaceTitle.trim()) { setInviteError('Space name is required.'); return; }
    if (inviteSpaceType === 'professional' && !inviteConsentChecked) { setInviteError('Consent is required for Professional spaces.'); return; }
    setInviteError('');
    sendSharedInvite();
  }

  const inputStyle = { width: '100%', background: '#111', border: '1px solid #272736', borderRadius: 6, color: '#999', fontFamily: 'Georgia, serif', fontSize: 12, padding: '8px 10px', outline: 'none', boxSizing: 'border-box' };
  const labelStyle = { color: '#888', fontSize: 12, marginBottom: 6, fontFamily: 'Georgia, serif', display: 'block' };

  return (
    <div className="shift-modal-overlay" style={{
      zIndex: 2000,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20, fontFamily: 'Georgia, serif',
    }} onClick={e => { if (e.target === e.currentTarget) setShowInviteModal(false); }}>

      <div style={{
        background: '#07070a', border: '1px solid #272736',
        borderRadius: 12, width: 460, maxHeight: '85vh',
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
      }}>

        {/* Header */}
        <div style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '0.5px solid #1e1e2a', flexShrink: 0 }}>
          <span style={{ color: '#888', fontSize: 14, fontWeight: 'bold' }}>Invite to Space</span>
          <div onClick={() => setShowInviteModal(false)} style={{ cursor: 'pointer', color: '#333', fontSize: 18, lineHeight: 1, padding: '0 4px' }}
            onMouseEnter={e => e.currentTarget.style.color = '#888'}
            onMouseLeave={e => e.currentTarget.style.color = '#333'}>×</div>
        </div>

        {/* Body — scrollable single form */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* 1. Space type — types/colors/labels/descriptions imported from lib/space-types.js */}
          <div>
            <div style={labelStyle}>What kind of Space?</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {SPACE_TYPE_ORDER.map(id => {
                const t = SPACE_TYPES[id];
                return (
                  <div key={id} onClick={() => {
                    setInviteSpaceType(id);
                    setInviteShiftMode?.(id === 'facilitated' ? 'active' : 'observer');
                  }} style={{
                    border: `1px solid ${inviteSpaceType === id ? t.color + '55' : '#272736'}`,
                    borderRadius: 8, padding: '10px 10px', cursor: 'pointer',
                    background: inviteSpaceType === id ? t.color + '08' : '#111',
                  }}>
                    <div style={{ fontSize: 12, color: t.color, marginBottom: 3, fontWeight: 500 }}>{t.label}</div>
                    <div style={{ fontSize: 11, color: '#666', lineHeight: 1.5 }}>{t.desc}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 2. Email */}
          <div>
            <div style={labelStyle}>Their email <span style={{ color: '#c46050' }}>*</span></div>
            <input type="email" value={inviteEmail}
              onChange={e => { setInviteEmail(e.target.value); if (inviteError) setInviteError(''); }}
              placeholder="colleague@example.com" style={inputStyle} />
          </div>

          {/* 3. Space name */}
          <div>
            <div style={labelStyle}>Space name <span style={{ color: '#c46050' }}>*</span></div>
            <input value={inviteSpaceTitle}
              onChange={e => { setInviteSpaceTitle(e.target.value); if (inviteError) setInviteError(''); }}
              placeholder="e.g. Q2 Strategy, Weekly 1:1, Dad & Me" style={inputStyle} />
          </div>

          {/* 4. Role */}
          <div>
            <div style={labelStyle}>Your role</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 5 }}>
              {['Coach', 'Coachee', 'Manager', 'Employee', 'Teacher', 'Advisor', 'Mentor', 'Friend', 'Other...'].map(r => (
                <div key={r} onClick={() => setInviteSpaceProfRole(r)} style={{
                  background: inviteSpaceProfRole === r ? 'rgba(122,158,130,0.04)' : '#111',
                  border: `1px solid ${inviteSpaceProfRole === r ? '#7a9e8255' : '#272736'}`,
                  borderRadius: 6, padding: '6px 4px', cursor: 'pointer',
                  color: inviteSpaceProfRole === r ? '#7a9e82' : '#444',
                  fontSize: 10, textAlign: 'center',
                }}>{r}</div>
              ))}
            </div>
            {inviteSpaceProfRole === 'Other...' && (
              <input value={customRole} onChange={e => setCustomRole(e.target.value)}
                placeholder="e.g. Father, Husband, Partner..."
                style={{ ...inputStyle, fontSize: 11, marginTop: 6 }} />
            )}
          </div>

          {/* 5. Personal note */}
          <div>
            <div style={labelStyle}>Personal note <span style={{ color: '#333', fontStyle: 'italic' }}>(optional)</span></div>
            <textarea value={inviteSpaceComment} onChange={e => setInviteSpaceComment(e.target.value)}
              rows={2} placeholder="Why you're inviting them..."
              style={{ ...inputStyle, resize: 'none', lineHeight: 1.6 }} />
          </div>

          {/* 6. Consent — professional only */}
          {inviteSpaceType === 'professional' && (
            <div onClick={() => setInviteConsentChecked(!inviteConsentChecked)} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
              <div style={{
                width: 13, height: 13, border: `1px solid ${inviteConsentChecked ? '#7a9e82' : '#2a2a2a'}`,
                borderRadius: 3, flexShrink: 0, marginTop: 2,
                background: inviteConsentChecked ? '#7a9e82' : 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {inviteConsentChecked && <svg width="7" height="7" viewBox="0 0 10 10" fill="none"><path d="M2 5l2.5 2.5L8 2.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              </div>
              <span style={{ color: '#555', fontSize: 10, lineHeight: 1.6 }}>I have consent to share this person's information with an AI system.</span>
            </div>
          )}

          {/* Error / Success */}
          {inviteError && <div style={{ color: '#c46050', fontSize: 11, fontStyle: 'italic' }}>{inviteError}</div>}
          {inviteSuccess && <div style={{ color: '#7a9e82', fontSize: 11, fontStyle: 'italic' }}>{inviteSuccess}</div>}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '0.5px solid #1e1e2a', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexShrink: 0 }}>
          <button onClick={handleSend} disabled={inviteSending}
            style={{ background: 'none', border: '1px solid #7a9e8233', borderRadius: 6, color: '#7a9e82', fontSize: 11, fontFamily: 'Georgia, serif', padding: '6px 16px', cursor: 'pointer', opacity: inviteSending ? 0.5 : 1 }}
            onMouseEnter={e => { if (!inviteSending) e.currentTarget.style.background = 'rgba(122,158,130,0.06)'; }}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
            {inviteSending ? 'Sending...' : 'Send invite →'}
          </button>
        </div>

      </div>
    </div>
  );
}
